import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Package } from 'lucide-react';
import { useShopAuth } from '../../context/ShopAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatRWF = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n);

const STATUS_LABEL = {
  pending_payment: 'Pending payment',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  dispatched: 'Dispatched',
  en_route: 'En route',
  delivered: 'Delivered',
  received: 'Received',
  cancelled: 'Cancelled',
};

export default function ShopAccount() {
  const { customer, loading, authHeader, setCustomer } = useShopAuth();
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [pw, setPw] = useState({ current_password: '', new_password: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (customer) {
      setForm({ name: customer.name || '', phone: customer.phone || '' });
      axios.get(`${API}/shop/orders/mine`, { headers: authHeader() }).then(r => setOrders(r.data)).catch(() => {});
    }
  }, [customer]);

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!customer) return <Navigate to="/shop/login" replace />;

  const saveProfile = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/shop/auth/profile`, form, { headers: authHeader() });
      setCustomer(r.data);
      toast.success('Profile updated');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const changePw = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/shop/auth/change-password`, pw, { headers: authHeader() });
      setPw({ current_password: '', new_password: '' });
      toast.success('Password changed');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <h1 className="text-3xl font-serif font-bold">My account</h1>

      {/* Profile */}
      <section className="p-5 bg-card rounded-xl border border-border" data-testid="account-profile">
        <h2 className="font-semibold mb-3">Profile</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} data-testid="profile-name" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm(f => ({...f, phone: e.target.value}))} data-testid="profile-phone" /></div>
          <div className="sm:col-span-2"><Label>Email</Label><Input value={customer.email} disabled /></div>
        </div>
        <Button onClick={saveProfile} disabled={busy} className="mt-3" data-testid="profile-save-btn">Save</Button>
      </section>

      {/* Password */}
      <section className="p-5 bg-card rounded-xl border border-border" data-testid="account-password">
        <h2 className="font-semibold mb-3">Change password</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Current password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw(p => ({...p, current_password: e.target.value}))} data-testid="pw-current" /></div>
          <div><Label>New password</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw(p => ({...p, new_password: e.target.value}))} data-testid="pw-new" /></div>
        </div>
        <Button variant="outline" onClick={changePw} disabled={busy || !pw.current_password || !pw.new_password} className="mt-3" data-testid="pw-change-btn">Change password</Button>
      </section>

      {/* Orders */}
      <section className="p-5 bg-card rounded-xl border border-border" data-testid="account-orders">
        <h2 className="font-semibold mb-3 flex items-center gap-2"><Package className="w-4 h-4" />My orders</h2>
        {orders.length === 0 ? (
          <p className="text-sm text-muted-foreground">You have no orders yet. <Link to="/shop/products" className="text-primary hover:underline">Start shopping</Link>.</p>
        ) : (
          <div className="space-y-2">
            {orders.map(o => (
              <Link key={o.id} to={`/shop/orders/${o.id}`} className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary transition" data-testid={`order-row-${o.id}`}>
                <div>
                  <div className="font-medium">Order #{o.order_number}</div>
                  <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()} · {o.items.length} item(s)</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">RWF {formatRWF(o.total)}</div>
                  <div className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary inline-block mt-1">{STATUS_LABEL[o.status] || o.status}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
